import TopPostCard from '@frontend/applications/Social/Analytics/components/tabs/top-post-card';
import { CrossTabNavigationOptions, useTopPostsData } from '@frontend/applications/Social/Analytics/hooks';
import { PostCard, PostCardSkeleton, PostCardMedia } from '@frontend/applications/Social/components/post-card';
import { calculateDatesFromType } from '@frontend/applications/Social/utils/dates';
import { useAuth } from '@frontend/context/authContext';
import React, { useMemo } from 'react';
import { Empty } from '@frontend/app/refresh-components';
import { ImageSlashIcon } from '@revfluence/fresh-icons/solid/esm';
import { Button } from '@frontend/shadcn/components/ui/button';
import { useHistory } from 'react-router-dom';

interface PostsProps {
  projectIds: number[];
}

export const Posts = ({ projectIds }: PostsProps) => {
  const { clientInfo } = useAuth();
  const history = useHistory();
  const { startDate, endDate } = calculateDatesFromType('allTime');

  const { data: topPostsData, fetching: topPostsFetching } = useTopPostsData({
    clientId: clientInfo.id,
    queryVariables: {
      clientId: clientInfo.id,
      startDate: startDate.toISOString().split('T')[0],
      endDate: endDate.toISOString().split('T')[0],
      includeEstimates: true,
      networks: [],
      postTypes: [],
      groupIds: [],
      includeAds: true,
      lifetimeInsightsEnabled: false,
      memberIds: [],
      projectIds,
      mentionSources: [],
      postDateIntervalEnabled: false,
      segments: [],
    },
    pause: !clientInfo.id,
  });

  const content = useMemo(() => {
    const topPosts = topPostsData?.client?.socialAnalytics?.posts?.results;

    const filterParams: CrossTabNavigationOptions['additionalParams'] = {
      projectIds: projectIds.join(','),
      dateType: 'allTime',
      includeEstimates: 'true',
      postDateIntervalEnabled: 'false',
      lifetimeInsightsEnabled: 'false',
      viewMode: 'cards',
    };

    if (topPostsFetching) {
      return (
        <div className="flex gap-4 flex-wrap">
          {Array.from({ length: 4 }).map((_, idx) => (
            <div
              key={idx}
              className="w-[280px] flex-shrink-0"
              role="listitem"
            >
              <PostCard>
                <PostCardMedia>
                  <PostCardSkeleton />
                </PostCardMedia>
              </PostCard>
            </div>
          ))}
        </div>
      );
    }

    if (!topPosts?.length) {
      return (
        <div className="p-4">
          <Empty
            icon={ImageSlashIcon}
            title="No top posts available"
            description="We couldn't find any top posts for your projects at this time."
            layout="horizontal"
            size="lg"
          />
        </div>
      );
    }

    return (
      <div className="flex flex-col gap-4 items-start">
        <div className="flex gap-4 flex-wrap">
          {topPosts?.slice(0, 4).map((post) => (
            <div
              key={`${post.entityId}-${post.entityNetwork}`}
              className="w-[280px] flex-shrink-0"
              role="listitem"
            >
              <TopPostCard
                post={post}
                additionalParams={filterParams}
              />
            </div>
          ))}
        </div>
        <Button
          variant="link"
          size="sm"
          onClick={() => {
            history.push({
              pathname: '/social/analytics/posts',
              search: new URLSearchParams(filterParams).toString(),
            });
          }}
        >
          View More
        </Button>
      </div>
    );
  }, [topPostsData, projectIds, topPostsFetching, history]);

  return (
    content
  );
};
